import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, ShieldCheck, Plug, Undo2, Mail } from "lucide-react";
import { toast } from "sonner";
import { EmailDiagnostics } from "@/components/admin/EmailHealth";

const sel = "h-9 w-full rounded-md border bg-white px-2 text-sm";
const F = ({ label, children }) => <div><Label className="text-xs font-semibold">{label}</Label><div className="mt-1">{children}</div></div>;

export default function EmailSettingsTab() {
  const [data, setData] = useState(null);
  const [c, setC] = useState(null);
  const [pw, setPw] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const load = () => api.get("/admin/email/settings").then((r) => { setData(r.data); setC(r.data.active); });
  useEffect(() => { load(); }, []);
  if (!c) return <Loader2 className="animate-spin" />;
  const P = data.providers;
  const smtp = P[c.provider]?.smtp;
  const set = (k, v) => setC({ ...c, [k]: v });
  const pick = (p) => setC({ ...c, provider: p, ...(P[p].host !== undefined && p === "smtp2go" ? { host: P[p].host, port: P[p].port, encryption: P[p].encryption } : {}) });
  const run = async (k, fn) => { setBusy(k); setResult(null); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); } finally { setBusy(""); } };
  const check = () => run("check", async () => { const r = await api.post("/admin/email/settings/check", { config: c, password: pw }); setResult(r.data); });
  const activate = () => run("activate", async () => {
    const r = await api.post("/admin/email/settings/activate", { config: c, password: pw, test_to: testTo });
    setResult(r.data);
    if (r.data.ok) { toast.success("Validated and activated — check the test inbox"); setPw(""); load(); }
  });
  const restore = () => run("restore", async () => { const r = await api.post("/admin/email/settings/restore-previous"); setResult(r.data); if (r.data.ok) { toast.success("Previous provider restored"); load(); } });
  const a = data.active;

  return (
    <div className="max-w-3xl space-y-6" data-testid="email-settings-tab">
      <div>
        <h2 className="text-2xl font-extrabold text-steel flex items-center gap-2"><Mail size={22} /> Email Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">One provider sends every website email (enquiries, orders, receipts, account emails, reports). If it fails, emails are queued and retried through the same provider — never another one.</p>
      </div>
      <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3 text-sm" data-testid="email-active-provider">
        Active: <b>{P[a.provider].label}</b>{a.provider !== "emergent" && <> · {a.host}:{a.port} · {a.encryption.toUpperCase()} · from {a.from_email}</>}
        {a.activated_at && <span className="text-muted-foreground"> · activated {new Date(a.activated_at).toLocaleString()} by {a.activated_by}</span>}
      </div>
      <div className="rounded-xl border bg-white p-5 space-y-4">
        <F label="Provider">
          <select value={c.provider} onChange={(e) => pick(e.target.value)} className={sel} data-testid="email-provider-select">
            {Object.entries(P).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </F>
        {smtp && (
          <>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><F label="SMTP host"><Input value={c.host} onChange={(e) => set("host", e.target.value)} data-testid="email-host" /></F></div>
              <F label="Port"><Input type="number" value={c.port} onChange={(e) => set("port", e.target.value)} data-testid="email-port" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="SMTP username"><Input value={c.username} onChange={(e) => set("username", e.target.value)} autoComplete="off" data-testid="email-username" /></F>
              <F label={a.has_password && a.provider === c.provider ? "SMTP password (saved — leave blank to keep)" : "SMTP password"}>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" placeholder={a.has_password && a.provider === c.provider ? "••••••••" : ""} data-testid="email-password" /></F>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <F label="Encryption">
                <select value={c.encryption} onChange={(e) => set("encryption", e.target.value)} className={sel} data-testid="email-encryption">
                  <option value="starttls">STARTTLS</option><option value="ssl">SSL/TLS</option><option value="none">None</option>
                </select></F>
              <F label="Timeout (seconds)"><Input type="number" value={c.timeout} onChange={(e) => set("timeout", e.target.value)} data-testid="email-timeout" /></F>
              <div className="flex flex-col gap-2 pt-5 text-sm">
                <label className="flex items-center gap-2"><Switch checked={c.require_tls} onCheckedChange={(v) => set("require_tls", v)} data-testid="email-require-tls" /> Require TLS</label>
                <label className="flex items-center gap-2"><Switch checked={c.auth} onCheckedChange={(v) => set("auth", v)} data-testid="email-auth" /> Authentication</label>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Sender email (verified in your provider)"><Input value={c.from_email} onChange={(e) => set("from_email", e.target.value)} data-testid="email-from-email" /></F>
              <F label="Sender name"><Input value={c.from_name} onChange={(e) => set("from_name", e.target.value)} data-testid="email-from-name" /></F>
            </div>
          </>
        )}
        {!smtp && <F label="Sender name"><Input value={c.from_name} onChange={(e) => set("from_name", e.target.value)} data-testid="email-from-name" /></F>}
        <F label="Reply-to email"><Input value={c.reply_to} onChange={(e) => set("reply_to", e.target.value)} data-testid="email-reply-to" /></F>
        <F label="Send the validation test email to"><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@acponline.co.nz" data-testid="email-test-to" /></F>
        <div className="flex flex-wrap gap-2">
          {smtp && <Button variant="outline" onClick={check} disabled={!!busy} data-testid="email-check-connection">{busy === "check" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plug size={14} className="mr-1" />} Test connection &amp; login</Button>}
          <Button onClick={activate} disabled={!!busy} className="bg-accent text-steel hover:bg-amber-600 font-semibold" data-testid="email-activate">
            {busy === "activate" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />} Validate, send test &amp; activate
          </Button>
          {data.previous && <Button variant="outline" onClick={restore} disabled={!!busy} data-testid="email-restore-previous"><Undo2 size={14} className="mr-1" /> Restore previous ({P[data.previous.provider].label})</Button>}
        </div>
        {result && (
          <p className={`text-sm ${result.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="email-settings-result">
            {result.ok ? "Success." : `Not activated — ${result.message}. The current provider is still active.`}
          </p>
        )}
      </div>
      <div className="rounded-xl border bg-white p-5"><h3 className="font-bold text-steel mb-3">Delivery log &amp; test</h3><EmailDiagnostics /></div>
    </div>
  );
}
